/**
 * Turns the 3 panel results `api.ts#vitalityPanels` fetches into the 7
 * cards' numbers: the 4 measured metrics (sleep/activity/hrv/stress) from
 * the batch's own combined `trend` panel — each metric's own `delta`, and
 * its own status from `trend.metric_status` (`php/api_statistics.php` now
 * computes a per-metric contributor count for a multi-metric panel, so a
 * metric few people share, e.g. HRV, can't hide behind another metric's
 * contributors the way a single blanket panel status used to let it — see
 * `DashboardPanelResult.metric_status`'s own doc comment) —
 * `restingHeartRate` from the two `heart_rate` panels' bucket minimums, and
 * `vitalityScore`/`recovery` as a composite of the others
 * (`metrics.ts#compositeScore`). Every card carries its own `status`: a
 * measured card's own metric/panel status, or a composite card's combined
 * status from its constituents (`combineStatuses`).
 */

import type { DashboardPanelResult } from '@/features/statistics/api';
import {
  compositeScore,
  levelFor,
  normalize,
  type CardId,
  type Level,
} from '@/features/vitality/metrics';

export type PanelStatus = 'ok' | 'forbidden' | 'insufficient_data';

export interface CardData {
  id: CardId;
  /** `null` when the underlying metric has no data at all in range (still
   * "ok" status — an empty series, not forbidden/insufficient_data) — or
   * whenever `status` itself isn't `'ok'`. */
  value: number | null;
  previous: number | null;
  level: Level | null;
  /** This card's own outcome: a measured card's own metric/panel status, or
   * a composite card's (`recovery`/`vitalityScore`) combined status from its
   * constituent cards — see `combineStatuses`. */
  status: PanelStatus;
}

function dayCount(from?: string, to?: string): number {
  if (!from || !to) return 1;
  const days = Math.round(
    (new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime()) / 86_400_000,
  );
  return Math.max(1, days + 1);
}

/** A panel's own status, falling back to `insufficient_data` for a panel
 * that hasn't arrived yet (`undefined`, still loading, or simply not part of
 * a particular caller's batch) — the same "unknown defaults to the
 * conservative, privacy-safe state" fallback `VitalityPage.tsx`'s previous
 * `trendStatus`/`restingStatus` used. */
function panelStatus(panel: DashboardPanelResult | undefined): PanelStatus {
  return panel?.status ?? 'insufficient_data';
}

/** One metric's own status within the combined `trend` panel: the panel's
 * own status when it isn't `ok` (a `forbidden`/`insufficient_data` panel
 * applies to every metric it would have carried), otherwise that metric's
 * own entry in `trend.metric_status` — falling back to `ok` when the field
 * is absent (an `ok` panel with no breakdown at all means every requested
 * metric individually cleared the threshold; see
 * `php/api_statistics.php`'s `resolveDepartmentTarget`). */
function metricStatus(panel: DashboardPanelResult | undefined, metricKey: string): PanelStatus {
  const status = panelStatus(panel);
  if (status !== 'ok') return status;
  return panel?.metric_status?.[metricKey] ?? 'ok';
}

/**
 * The status a composite card (`recovery`/`vitalityScore`) reports from its
 * constituent cards' own statuses, in priority order: `forbidden` if any one
 * constituent is forbidden; else `insufficient_data` if any one constituent
 * is insufficient_data; else `ok`, only once every constituent is `ok`.
 * Since `panelStatus` above already folds a not-yet-arrived panel into
 * `insufficient_data`, an undetermined (still loading) constituent is
 * naturally treated the same way here — never crashes, never claims `ok`
 * for a metric the composite hasn't actually heard back on.
 */
function combineStatuses(statuses: readonly PanelStatus[]): PanelStatus {
  if (statuses.some((s) => s === 'forbidden')) return 'forbidden';
  if (statuses.some((s) => s === 'insufficient_data')) return 'insufficient_data';
  return 'ok';
}

function card(
  id: CardId,
  value: number | null,
  previous: number | null,
  status: PanelStatus,
): CardData {
  return { id, value, previous, level: status === 'ok' ? levelFor(id, value) : null, status };
}

/**
 * `trend` = the combined `compare_to: previous_period` panel (sleep_duration,
 * steps, hrv, stress). `restingCurrent`/`restingPrevious` = the two
 * standalone `heart_rate` panels (one per period, see `api.ts` for why
 * resting heart rate isn't in the `trend` panel's own delta). Returns `null`
 * only when none of the 3 panels have arrived yet (nothing to derive from at
 * all) — once any have, every card gets its own real value/`null` and its
 * own `status` (via `trend.metric_status`), rather than the whole result
 * collapsing to `null` just because one metric is privacy-gated or
 * forbidden.
 */
export function deriveCards(
  trend: DashboardPanelResult | undefined,
  restingCurrent: DashboardPanelResult | undefined,
  restingPrevious: DashboardPanelResult | undefined,
): Record<CardId, CardData> | null {
  if (!trend && !restingCurrent && !restingPrevious) {
    return null;
  }

  const sleepStatus = metricStatus(trend, 'sleep_duration');
  const activityStatus = metricStatus(trend, 'steps');
  const hrvStatus = metricStatus(trend, 'hrv');
  const stressStatus = metricStatus(trend, 'stress');
  const restingStatus = panelStatus(restingCurrent);

  const days = dayCount(trend?.from, trend?.to);
  const delta = trend?.delta ?? {};

  const sleepDelta = sleepStatus === 'ok' ? delta.sleep_duration : undefined;
  const activityDelta = activityStatus === 'ok' ? delta.steps : undefined;
  const hrvDelta = hrvStatus === 'ok' ? delta.hrv : undefined;
  const stressDelta = stressStatus === 'ok' ? delta.stress : undefined;

  const sleep = card(
    'sleep',
    sleepDelta?.current != null ? sleepDelta.current / days : null,
    sleepDelta?.previous != null ? sleepDelta.previous / days : null,
    sleepStatus,
  );
  const activity = card(
    'activity',
    activityDelta?.current != null ? activityDelta.current / days : null,
    activityDelta?.previous != null ? activityDelta.previous / days : null,
    activityStatus,
  );
  const hrv = card('hrv', hrvDelta?.current ?? null, hrvDelta?.previous ?? null, hrvStatus);
  const stress = card(
    'stress',
    stressDelta?.current ?? null,
    stressDelta?.previous ?? null,
    stressStatus,
  );

  const restingHeartRate = card(
    'restingHeartRate',
    restingHeartRateFromSeries(restingCurrent),
    restingHeartRateFromSeries(restingPrevious),
    restingStatus,
  );

  const recoveryStatus = combineStatuses([sleepStatus, hrvStatus, stressStatus]);
  const recoveryCurrent =
    recoveryStatus === 'ok'
      ? compositeScore([
          normalize('sleep', sleep.value),
          normalize('hrv', hrv.value),
          normalize('stress', stress.value),
        ])
      : null;
  const recoveryPrevious =
    recoveryStatus === 'ok'
      ? compositeScore([
          normalize('sleep', sleep.previous),
          normalize('hrv', hrv.previous),
          normalize('stress', stress.previous),
        ])
      : null;
  const recovery = card('recovery', recoveryCurrent, recoveryPrevious, recoveryStatus);

  const vitalityScoreStatus = combineStatuses([
    sleepStatus,
    activityStatus,
    hrvStatus,
    stressStatus,
    restingStatus,
  ]);
  const vitalityScoreCurrent =
    vitalityScoreStatus === 'ok'
      ? compositeScore([
          normalize('sleep', sleep.value),
          normalize('activity', activity.value),
          normalize('hrv', hrv.value),
          normalize('stress', stress.value),
          normalize('restingHeartRate', restingHeartRate.value),
        ])
      : null;
  const vitalityScorePrevious =
    vitalityScoreStatus === 'ok'
      ? compositeScore([
          normalize('sleep', sleep.previous),
          normalize('activity', activity.previous),
          normalize('hrv', hrv.previous),
          normalize('stress', stress.previous),
          normalize('restingHeartRate', restingHeartRate.previous),
        ])
      : null;
  const vitalityScore = card(
    'vitalityScore',
    vitalityScoreCurrent,
    vitalityScorePrevious,
    vitalityScoreStatus,
  );

  return { sleep, activity, hrv, stress, restingHeartRate, recovery, vitalityScore };
}

/**
 * Rusthartslag (WP-02 open point, resolved 2026-09-18): a **proxy**, not a
 * dedicated metric — `php/stats_metrics.php` has no resting-heart-rate
 * ingest, only `heart_rate`. The lowest value in the panel's single
 * `range`-grain bucket (`SeriesBucket.min`, tracked regardless of the
 * metric's own aggregation) approximates "lowest heart rate in a rest
 * window": not a true rest-window detector, but a reasonable, honestly
 * documented stand-in until a dedicated metric exists — the alternative
 * this WP considered was a placeholder ("not available") state, rejected
 * because the raw data to do better already exists.
 */
export function restingHeartRateFromSeries(
  result: DashboardPanelResult | undefined,
): number | null {
  if (!result || result.status !== 'ok') return null;
  const series = result.series?.find((s) => s.metric_key === 'heart_rate');
  const min = series?.buckets[0]?.min;
  return min ?? null;
}
